import { useEffect, useState } from "react";
import { ArrowSquareOut } from "@phosphor-icons/react";
import { ACCOMMODATIONS } from "@/lib/site";
import { getBookingUrl } from "@/lib/bookingConfig";

/**
 * Lightweight "we're sending you to our booking partner" page. Performs an
 * immediate redirect to the RMS Cloud hosted booking engine with selections
 * pre-filled. Shows a manual link as a fallback in case auto-redirect is
 * blocked (rare — but covers pop-up blockers and very strict browsers).
 */
export default function RmsRedirect({ initial }) {
  const { package_id, arrival, departure, adults, children } = initial || {};
  const [url, setUrl] = useState(null);
  const [phase, setPhase] = useState("looking-up"); // looking-up | redirecting | fallback

  const pkg = ACCOMMODATIONS.find((a) => a.id === package_id) || ACCOMMODATIONS[0];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await getBookingUrl({
        category: pkg?.rms_category_id || "",
        arrival,
        departure,
        adults,
        children,
      });
      if (cancelled) return;
      if (u) {
        setUrl(u);
        setPhase("redirecting");
        // Small delay so the user sees we're handing off (good UX + helps screen readers)
        setTimeout(() => {
          window.location.href = u;
        }, 600);
      } else {
        setPhase("fallback");
      }
    })();
    return () => { cancelled = true; };
  }, [pkg?.rms_category_id, arrival, departure, adults, children]);

  return (
    <section
      className="max-w-3xl mx-auto px-6 md:px-10 py-32 text-center"
      data-testid="book-page-rms"
    >
      <div className="overline">Booking</div>
      <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[0.95] mt-4">
        {phase === "fallback"
          ? "Booking engine unavailable"
          : "Taking you to our booking engine…"}
      </h1>

      <p className="mt-6 text-sa-ink2 text-lg max-w-xl mx-auto leading-relaxed">
        {phase === "fallback"
          ? "Online booking isn't quite ready yet — please send us an enquiry and we'll lock in your dates by reply."
          : `We're handing you off to our secure booking partner to confirm your ${pkg.name.toLowerCase()} stay. You'll be back here shortly.`}
      </p>

      {phase !== "fallback" && (
        <div className="mt-12 flex flex-col items-center gap-6">
          <div className="w-10 h-10 rounded-full border-2 border-sa-ink/15 border-t-sa-accent animate-spin" />
          {url && (
            <a
              href={url}
              className="inline-flex items-center gap-2 text-sa-accent hover:text-sa-accentHover text-sm"
              data-testid="rms-manual-link"
            >
              Continue manually if not redirected
              <ArrowSquareOut size={14} weight="bold" />
            </a>
          )}
        </div>
      )}

      {phase === "fallback" && (
        <div className="mt-10 flex items-center justify-center gap-3">
          <a href="/contact" className="btn-primary" data-testid="rms-fallback-enquiry">
            Send an enquiry
          </a>
          <a href="/" className="btn-ghost">Back to home</a>
        </div>
      )}
    </section>
  );
}
